// @ts-nocheck
import React, { useState, useEffect } from "react";
import ProcessedScatterGraph from "../forecastGraphs/ProcessedScatterGraph";
import { SkeletonChart } from "../Skeleton";
import {
  formatProcessedDataGraph,
  separatePercentileChannels,
} from "../../util/utils";
import {
  fetchAndParseForecastRecord,
  getVariationOptions,
} from "../../utils/modelOutputUtils";

import Select from "../ReusableComponents/Select";
interface ForecastRecordPreviewProps {
  s3Key: string;
}

const ForecastRecordPreview: React.FC<ForecastRecordPreviewProps> = ({
  s3Key,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{
    otherChannels: any;
    percentiles: any;
  } | null>(null);
  const [yUnit, setYUnit] = useState<string>("");
  const [variationOptions, setVariationOptions] = useState<
    { key: string; label: string }[]
  >([]);
  const [selectedVariation, setSelectedVariation] = useState<string>("");
  const [parsedData, setParsedData] = useState<any>(null);

  useEffect(() => {
    loadPreview(s3Key);
  }, [s3Key]);

  const loadPreview = async (key: string) => {
    setLoading(true);
    setError(null);
    setGraphData(null);

    try {
      const { parsedData: data, defaultModelKey } =
        await fetchAndParseForecastRecord(key);
      setParsedData(data);

      const options = getVariationOptions(data);
      setVariationOptions(options);
      setSelectedVariation(defaultModelKey);

      applyVariation(data, defaultModelKey);
    } catch (err: any) {
      console.error("Error loading forecast preview:", err);
      setError(err?.message || "Failed to load forecast data from S3");
    } finally {
      setLoading(false);
    }
  };

  const applyVariation = (data: any, variationKey: string) => {
    const models = data?.models || {};
    const modelData = models[variationKey];
    if (!modelData?.data) {
      setError("No data found for this variation");
      setGraphData(null);
      return;
    }

    const formatted = formatProcessedDataGraph(modelData.data);
    const { otherChannels, percentiles } = separatePercentileChannels(formatted);
    setGraphData({ otherChannels, percentiles });
    setYUnit(modelData.yUnit || "");
  };

  const handleVariationChange = (variationKey: string) => {
    setSelectedVariation(variationKey);
    if (parsedData) {
      applyVariation(parsedData, variationKey);
    }
  };

  if (loading) {
    return (
      <SkeletonChart bars={18} label="Loading forecast preview" withHeader={false} />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="font-inter text-eai-body-sm text-eai-danger-text">{error}</p>
      </div>
    );
  }

  if (!graphData) return null;

  return (
    <div className="space-y-3">
      {/* Variation selector */}
      {variationOptions.length > 1 && (
        <div className="flex items-center gap-2">
          <label className="eai-meta-label">
            Variation:
          </label>
          <Select
            value={selectedVariation}
            onChange={(e) => handleVariationChange(e.target.value)}
            size="sm"
          >
            {variationOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* Chart */}
      <div className="h-[350px] w-full">
        <ProcessedScatterGraph
          percentiles={graphData.percentiles}
          otherChannels={graphData.otherChannels}
          labels={[]}
          yaxis={yUnit}
          timeZone={"UTC"}
          assetTimeZone={"UTC"}
          timeZoneMode={"utc"}
          title={
            variationOptions.find((v) => v.key === selectedVariation)?.label ||
            "Forecast Preview"
          }
          hideGraphControls
          hideMobileControls
        />
      </div>
    </div>
  );
};

export default ForecastRecordPreview;
